import React, { useState, useMemo, useEffect, useRef, useContext, useCallback } from 'react';
import { Box, Typography, Grid, Stack, Divider, CircularProgress } from '@mui/material';
import { Timeline as MuiTimeline } from '@mui/lab';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import CitationPanel from './CitationPanel';
import { CitationProps } from './CitationPanel';
import Header from './Header';
import PreCutoffDisclaimer from './PreCutoffDisclaimer';
import { HealthTimelineSettings, CitationItem, ServerTimelineEvent, TimelineEvent } from './Interface';
import { HealthTimelineSettingsLabel, TimelineTypeToSettingsKey, getTimelineTypeFromDocumentType } from './Constants';
import CheckboxSettingsMenu from './CheckboxSettingsMenu';
import { TimelineItem } from './TimelineItem';
import { useRouteMatch } from 'react-router-dom';
import { AI_TIMELINE_QUERY, USER_ROLE_QUERY } from '../graphql';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import { isDemo } from 'op-utils';
import { getDocumentTypeIcon } from '../SummaryUtils';
import { NavigationContext } from 'op-contexts/index';
import { logDatadogRumCustomAction } from 'op-utils/datadogRumCustomAction';

const Timeline = ({
  data,
  compactOrStandard,
  settings,
  onRelatedItemClick,
  onCitationGroupSelection,
  selectedCitationGroupId,
  isSuperuser,
  patientId,
}: {
  data: TimelineEvent[];
  compactOrStandard: string;
  settings: HealthTimelineSettings;
  onRelatedItemClick: (content: CitationItem) => void;
  onCitationGroupSelection: (selectedGroupId: number | null) => void;
  selectedCitationGroupId: number | null;
  isSuperuser: boolean;
  patientId?: string;
}): JSX.Element => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const filteredData = useMemo(() => {
    // If no settings are selected (all false), show all items
    const hasSelectedSettings = Object.values(settings).some((value) => value === true);

    if (!hasSelectedSettings) {
      return data;
    }

    return data.filter((item) => {
      return settings[
        TimelineTypeToSettingsKey[item.type as keyof typeof TimelineTypeToSettingsKey] as keyof HealthTimelineSettings
      ];
    });
  }, [data, settings]);

  // Check if any filters are active
  const hasActiveFilters = Object.values(settings).some((value) => value === true);
  // Scroll to top when switching between compact and standard
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [compactOrStandard]);
  const newActivity = filteredData?.filter((item) => item.isNew);
  const previousActivity = filteredData?.filter((item) => !item.isNew);

  if (filteredData?.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '32px',
        }}>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
          No available information.
        </Typography>
      </Box>
    );
  }

  const animationDuration = filteredData?.length === 1 ? 1000 : 5000;
  return (
    <Box
      sx={{ flex: 1, minHeight: 0, overflow: 'hidden', paddingRight: '0px !important', marginRight: '0px !important' }}>
      <Box
        ref={scrollContainerRef}
        sx={{
          height: '100%',
          overflowY: 'auto',
          display: 'flex',
          justifyContent: 'center',
        }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: '786px',
            minHeight: '100%',
            '& .timeline-enter': {
              opacity: 1,
              maxHeight: '0',
              overflow: 'hidden',
              position: 'relative',
              zIndex: 3,
              backgroundColor: 'white',
            },
            '& .timeline-enter-active': {
              opacity: 1,
              maxHeight: '4000px',
              overflow: 'hidden',
              transitionProperty: 'max-height',
              transitionDuration: '6000ms',
              transitionTimingFunction: 'cubic-bezier(0, 0, 1, 1)',
              backgroundColor: 'white',
            },
            '& .timeline-exit': {
              opacity: 0.5,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              pointerEvents: 'none',
              zIndex: 1,
              clipPath: 'inset(0 0 0 0)',
            },
            '& .timeline-exit-active': {
              opacity: 0.5,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              pointerEvents: 'none',
              zIndex: 1,
              clipPath: 'inset(0 0 0 0)',
              transitionProperty: 'clip-path',
              transitionDuration: '6000ms',
              transitionTimingFunction: 'cubic-bezier(100%, 0, 1, 1)',
            },
          }}>
          <TransitionGroup component={null}>
            <CSSTransition
              key={compactOrStandard}
              timeout={animationDuration}
              classNames="timeline"
              onEnter={() => {
                setIsTransitioning(true);
                // Scroll to top before animation starts
                if (scrollContainerRef.current) {
                  scrollContainerRef.current.scrollTop = 0;
                }
              }}
              onEntered={() => setIsTransitioning(false)}
              onExit={() => setIsTransitioning(true)}
              onExited={() => setIsTransitioning(false)}>
              <Stack
                direction="column"
                sx={{
                  py: 2,
                  maxWidth: '786px',
                  padding: '16px 16px 48px 16px',
                  position: 'relative',
                }}>
                {!hasActiveFilters && isDemo && newActivity?.length > 0 ? (
                  <Typography
                    variant="overline"
                    sx={{ textAlign: 'left', color: 'text.secondary', paddingBottom: '8px' }}>
                    WHAT'S NEW?
                  </Typography>
                ) : (
                  <Box sx={{ paddingBottom: '12px', height: '36px' }} />
                )}
                <MuiTimeline
                  sx={{
                    '& .MuiTimelineItem-root:before': { flex: 0, padding: '0px', marginLeft: '0px' },
                    padding: '0px',
                    margin: '0px',
                  }}>
                  {hasActiveFilters ? (
                    // When filters are active, show all filtered items without sections
                    filteredData?.map((event, index) => (
                      <TimelineItem
                        key={index}
                        event={event}
                        index={index}
                        onRelatedItemClick={onRelatedItemClick}
                        isLastItem={index === filteredData.length - 1}
                        contentType={compactOrStandard}
                        contents={compactOrStandard === 'standard' ? event.standardContent : event.compactContent}
                        onCitationGroupSelection={onCitationGroupSelection}
                        selectedCitationGroupId={selectedCitationGroupId}
                        isSuperuser={isSuperuser}
                        patientId={patientId}
                      />
                    ))
                  ) : (
                    // When no filters are active, show the original sections
                    <>
                      {newActivity?.map((event, index) => (
                        <TimelineItem
                          key={index}
                          event={event}
                          index={index}
                          onRelatedItemClick={onRelatedItemClick}
                          isLastItem={index === filteredData.length - 1}
                          contentType={compactOrStandard}
                          contents={compactOrStandard === 'standard' ? event.standardContent : event.compactContent}
                          onCitationGroupSelection={onCitationGroupSelection}
                          selectedCitationGroupId={selectedCitationGroupId}
                          isSuperuser={isSuperuser}
                          patientId={patientId}
                        />
                      ))}
                      {isDemo && newActivity?.length > 0 && (
                        <Divider sx={{ margin: '16px 0px' }}>
                          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                            PREVIOUS ACTIVITY
                          </Typography>
                        </Divider>
                      )}
                      {previousActivity?.map((event, index) => (
                        <TimelineItem
                          key={index}
                          event={event}
                          index={index}
                          onRelatedItemClick={onRelatedItemClick}
                          isLastItem={index === previousActivity.length - 1}
                          contentType={compactOrStandard}
                          contents={compactOrStandard === 'standard' ? event.standardContent : event.compactContent}
                          onCitationGroupSelection={onCitationGroupSelection}
                          selectedCitationGroupId={selectedCitationGroupId}
                          isSuperuser={isSuperuser}
                          patientId={patientId}
                        />
                      ))}
                    </>
                  )}
                </MuiTimeline>
                {isTransitioning && (
                  <Box
                    className="timeline-transition-box"
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: '150px',
                      pointerEvents: 'none',
                      background: 'linear-gradient(to top, white, rgba(255, 255, 255, 0.9),  transparent)',
                      zIndex: 10,
                    }}
                  />
                )}
              </Stack>
            </CSSTransition>
          </TransitionGroup>
        </Box>
      </Box>
    </Box>
  );
};

const generateDataRelatedItemIds = (data: ServerTimelineEvent[]): TimelineEvent[] => {
  let relatedId = 0;
  let citationGroupId = 0;
  return data?.map((item) => ({
    type: getTimelineTypeFromDocumentType(item.type),
    date: dayjs(item.date).format(CurrentAppConfig.PatientCard.defaultAlternateDateFormat),
    title: item.title,
    isLatest: item.isLatest,
    isNew: item.isNew,
    resourceId: item.resourceId,
    traceId: item.traceId,
    spanId: item.spanId,
    standardContent: item.standard.map((timeline) => {
      return {
        id: citationGroupId++,
        text: timeline.text,
        citations: timeline.citations.map((citation) => {
          relatedId++;
          return {
            title: citation.title,
            documentUrl: citation.documentUrl,
            documentType: citation.documentType,
            icon: getDocumentTypeIcon(citation.documentType),
            author: citation?.authorName,
            status: citation?.status,
            dateOfDocument: (() => {
              if (!citation.dateOfDocument || citation.dateOfDocument.trim() === '') {
                return 'No Document Date';
              }
              const parsedDate = dayjs(citation.dateOfDocument);
              const isValid = parsedDate.isValid();
              const formattedDate = isValid
                ? parsedDate.format(CurrentAppConfig.PatientCard.defaultAlternateDateFormat)
                : 'No Document Date';
              return formattedDate;
            })(),
            notes: citation.notes,
            relatedItemId: relatedId,
            highlightGeoms: citation.highlightGeoms,
            citedText: citation?.citedText,
          };
        }),
        subItems: timeline.subItems?.map((subItem) => {
          return {
            id: citationGroupId++,
            text: subItem.text,
            citations: subItem.citations.map((citation) => {
              return {
                ...citation,
                icon: getDocumentTypeIcon(citation.documentType),
                author: citation?.authorName,
                status: citation?.status,
                dateOfDocument: dayjs(citation.dateOfDocument).format(
                  CurrentAppConfig.PatientCard.defaultAlternateDateFormat,
                ),
                relatedItemId: relatedId,
              };
            }),
          };
        }),
      };
    }),
    compactContent: item.compact.map((timeline) => {
      return {
        id: citationGroupId++,
        text: timeline.text,
        citations: timeline.citations.map((citation) => {
          relatedId++;
          return {
            title: citation.title,
            documentUrl: citation.documentUrl,
            documentType: citation.documentType,
            icon: getDocumentTypeIcon(citation.documentType),
            author: citation?.authorName,
            status: citation?.status,
            dateOfDocument: (() => {
              if (!citation.dateOfDocument || citation.dateOfDocument.trim() === '') {
                return 'No Document Date';
              }
              const parsedDate = dayjs(citation.dateOfDocument);
              const isValid = parsedDate.isValid();
              const formattedDate = isValid
                ? parsedDate.format(CurrentAppConfig.PatientCard.defaultAlternateDateFormat)
                : 'No Document Date';
              return formattedDate;
            })(),
            notes: citation.notes,
            relatedItemId: relatedId,
            highlightGeoms: citation.highlightGeoms,
            citedText: citation?.citedText,
          };
        }),
        subItems: timeline.subItems?.map((subItem) => {
          return {
            id: citationGroupId++,
            text: subItem.text,
            citations: subItem.citations.map((citation) => {
              relatedId++;
              return {
                ...citation,
                icon: getDocumentTypeIcon(citation.documentType),
                author: citation?.authorName,
                status: citation?.status,
                dateOfDocument: dayjs(citation.dateOfDocument).format(
                  CurrentAppConfig.PatientCard.defaultAlternateDateFormat,
                ),
                relatedItemId: relatedId,
              };
            }),
          };
        }),
      };
    }),
  }));
};

export const HealthTimelineContainer = (): JSX.Element => {
  const {
    params: { id: patientId },
  } = useRouteMatch<{ id: string }>();
  const { data: aiTimelineData, loading: aiTimelineLoading } = useQuery(AI_TIMELINE_QUERY, {
    variables: {
      patientId: patientId,
    },
    skip: !patientId,
  });
  const { data: userRoleData } = useQuery(USER_ROLE_QUERY);
  const [isLoading, setIsLoading] = useState(true);
  const [compactOrStandard, setCompactOrStandard] = useState('compact');
  const [citationContent, setCitationContent] = useState<CitationProps | null>(null);
  const [healthTimelineSettings, setHealthTimelineSettings] = useState<HealthTimelineSettings>({
    showImagingReports: false,
    showPathologyReports: false,
    showBloodTest: false,
    showPatientReported: false,
    showClinicalNotes: false,
    showLetters: false,
  });
  const healthTimelineSettingsRef = useRef(healthTimelineSettings);
  healthTimelineSettingsRef.current = healthTimelineSettings;

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [data, setData] = useState<TimelineEvent[]>();
  const [selectedCitationGroupId, setSelectedCitationGroupId] = useState<number | null>(null);
  const { setIsSideNavbarCollapsed } = useContext(NavigationContext);

  useEffect(() => {
    if (aiTimelineData) {
      setData(generateDataRelatedItemIds(aiTimelineData.aiTimeline?.timeline || []));
      setIsLoading(false);
    }
  }, [aiTimelineData]);

  const handleCitationContent = useCallback(
    (content: CitationItem | null) => {
      if (content) {
        logDatadogRumCustomAction('ai_timeline_citation_open', {
          patientId,
          viewMode: compactOrStandard as 'standard' | 'compact',
          documentType: content.documentType,
        });
        setIsSideNavbarCollapsed(true);
      } else {
        setIsSideNavbarCollapsed(false);
      }
      setCitationContent(content as CitationProps);
      setData((prevData) =>
        prevData?.map((item) => ({
          ...item,
          standardContent: item.standardContent.map((timeline) => {
            return {
              ...timeline,
              citations: timeline.citations.map((citation) => {
                return { ...citation, selected: citation.relatedItemId === content?.relatedItemId };
              }),
              subItems: timeline.subItems?.map((subItem) => {
                return {
                  ...subItem,
                  citations: subItem.citations.map((citation) => {
                    return { ...citation, selected: citation.relatedItemId === content?.relatedItemId };
                  }),
                };
              }),
            };
          }),
          compactContent: item.compactContent.map((timeline) => {
            return {
              ...timeline,
              citations: timeline.citations.map((citation) => {
                return { ...citation, selected: citation.relatedItemId === content?.relatedItemId };
              }),
              subItems: timeline.subItems?.map((subItem) => {
                return {
                  ...subItem,
                  citations: subItem.citations.map((citation) => {
                    return { ...citation, selected: citation.relatedItemId === content?.relatedItemId };
                  }),
                };
              }),
            };
          }),
        })),
      );
    },
    [patientId, setIsSideNavbarCollapsed, compactOrStandard],
  );

  const handleHealthTimelineSettingsChange = useCallback(
    (settings: HealthTimelineSettings) => {
      const prev = healthTimelineSettingsRef.current;
      const changedKeys = (Object.keys(settings) as (keyof HealthTimelineSettings)[]).filter(
        (key) => prev[key] !== settings[key],
      );
      setHealthTimelineSettings(settings);
      changedKeys.forEach((key) => {
        logDatadogRumCustomAction(settings[key] ? 'ai_timeline_setting_enable' : 'ai_timeline_setting_disable', {
          patientId,
          viewMode: compactOrStandard as 'standard' | 'compact',
          key: String(key),
        });
      });
    },
    [patientId, compactOrStandard],
  );

  const handleCompact = useCallback(() => {
    logDatadogRumCustomAction('ai_timeline_view_compact', {
      patientId,
      viewMode: 'compact',
    });
    setCompactOrStandard('compact');
  }, [patientId]);

  const handleStandard = useCallback(() => {
    logDatadogRumCustomAction('ai_timeline_view_standard', {
      patientId,
      viewMode: 'standard',
    });
    setCompactOrStandard('standard');
  }, [patientId]);

  return (
    <Grid container>
      <Grid
        item
        xs={citationContent ? 6 : 12}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'all .5s ease-in-out' }}>
        <Stack sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Header
            title="Timeline"
            onCompact={handleCompact}
            onStandard={handleStandard}
            compactOrStandard={compactOrStandard}
            settingsMenu={
              <CheckboxSettingsMenu
                anchorEl={menuAnchorEl}
                handleMenuClose={() => {
                  setMenuAnchorEl(null);
                }}
                settings={healthTimelineSettings}
                setSettings={handleHealthTimelineSettingsChange}
                settingsLabel={HealthTimelineSettingsLabel}
              />
            }
            setMenuAnchorEl={setMenuAnchorEl}
          />
          <PreCutoffDisclaimer patientId={patientId} />
          {isLoading || aiTimelineLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Timeline
              data={data || []}
              compactOrStandard={compactOrStandard}
              settings={healthTimelineSettings}
              onRelatedItemClick={handleCitationContent}
              onCitationGroupSelection={setSelectedCitationGroupId}
              selectedCitationGroupId={selectedCitationGroupId}
              isSuperuser={userRoleData?.user?.isSuperuser || false}
              patientId={patientId}
            />
          )}
        </Stack>
      </Grid>
      <Grid item xs={citationContent ? 6 : 0}>
        <CitationPanel
          onClose={() => {
            handleCitationContent(null);
          }}
          citation={citationContent!}
        />
      </Grid>
    </Grid>
  );
};

export default Timeline;
